export type EntregaDeliveryReadinessParticipante = {
  beneficiarioNombre?: string;
  beneficiarioDocumento?: string;
  entregaBolsa?: string | null;
  firmaMediaId?: string | null;
  fotoMediaId?: string | null;
  acudienteAutorizado?: string | null;
  cedulaBeneficiarioFrontMediaId?: string | null;
  cedulaBeneficiarioBackMediaId?: string | null;
  cedulaAutorizadoFrontMediaId?: string | null;
  cedulaAutorizadoBackMediaId?: string | null;
  documentoAutorizacionMediaId?: string | null;
};

export type EntregaDeliveryReadinessRemision = {
  firmaRecibeMediaId?: string | null;
  firmaEntregaMediaId?: string | null;
};

export type EntregaDeliveryReadinessInput = {
  participantes: EntregaDeliveryReadinessParticipante[];
  remisiones: EntregaDeliveryReadinessRemision[];
};

export type EntregaDeliveryReadinessIssue = {
  message: string;
};

function hasMediaId(id: string | null | undefined): boolean {
  return Boolean(id?.trim());
}

export function participanteDeliveryLabel(participante: EntregaDeliveryReadinessParticipante): string {
  const name = participante.beneficiarioNombre?.trim();
  const doc = participante.beneficiarioDocumento?.trim();
  if (name && doc) return `${name} (${doc})`;
  return name || doc || 'Participante';
}

/** UI badge helper: firma/foto always tracked (optional for sync/export/PDF). */
export function participanteRequiresDeliveryMedia(_participante?: { entregaBolsa?: string | null }): boolean {
  return true;
}

function acudienteDocMissingLabels(participante: EntregaDeliveryReadinessParticipante): string[] {
  if (participante.acudienteAutorizado !== 'si') return [];
  const missing: string[] = [];
  if (!hasMediaId(participante.cedulaBeneficiarioFrontMediaId)) missing.push('cédula del beneficiario (frontal)');
  if (!hasMediaId(participante.cedulaBeneficiarioBackMediaId)) missing.push('cédula del beneficiario (trasera)');
  if (!hasMediaId(participante.cedulaAutorizadoFrontMediaId)) missing.push('cédula del autorizado (frontal)');
  if (!hasMediaId(participante.cedulaAutorizadoBackMediaId)) missing.push('cédula del autorizado (trasera)');
  if (!hasMediaId(participante.documentoAutorizacionMediaId)) missing.push('documento de autorización');
  return missing;
}

export function entregaDeliveryReadinessIssues(
  input: EntregaDeliveryReadinessInput,
): EntregaDeliveryReadinessIssue[] {
  const issues: EntregaDeliveryReadinessIssue[] = [];

  for (const participante of input.participantes) {
    const label = participanteDeliveryLabel(participante);
    // ponytail: firma/foto = warning only; acudiente docs still block.
    const acudienteMissing = acudienteDocMissingLabels(participante);
    if (acudienteMissing.length > 0) {
      issues.push({ message: `${label} sin ${acudienteMissing.join(', ')}.` });
    }
  }

  return issues;
}

/** Non-blocking: missing firma/foto (participante + remisión). */
export function entregaDeliveryMediaWarnings(input: EntregaDeliveryReadinessInput): EntregaDeliveryReadinessIssue[] {
  const issues: EntregaDeliveryReadinessIssue[] = [];
  for (const participante of input.participantes) {
    if (!participanteRequiresDeliveryMedia(participante)) continue;
    const label = participanteDeliveryLabel(participante);
    const missing: string[] = [];
    if (!hasMediaId(participante.firmaMediaId)) missing.push('firma');
    if (!hasMediaId(participante.fotoMediaId)) missing.push('foto');
    if (missing.length > 0) issues.push({ message: `${label} sin ${missing.join(' ni ')}.` });
  }
  for (const [index, remision] of input.remisiones.entries()) {
    const missing: string[] = [];
    if (!hasMediaId(remision.firmaRecibeMediaId)) missing.push('firma de quien recibe');
    if (!hasMediaId(remision.firmaEntregaMediaId)) missing.push('firma de quien entrega');
    if (missing.length === 0) continue;
    const prefix = input.remisiones.length > 1 ? `Remisión ${index + 1}` : 'Remisión';
    issues.push({ message: `${prefix} sin ${missing.join(' ni ')}.` });
  }
  return issues;
}

export function formatEntregaDeliveryReadinessMessage(
  issues: EntregaDeliveryReadinessIssue[],
  opts?: { puntoNombre?: string; action?: 'pdf' | 'sync' },
): string {
  if (issues.length === 0) return '';
  const action = opts?.action === 'sync' ? 'sincronizar' : 'generar el PDF';
  const punto = opts?.puntoNombre?.trim();
  const head = punto ? `No se puede ${action} el punto «${punto}»:` : `No se puede ${action}:`;
  return `${head} ${issues.map((issue) => issue.message).join(' ')}`;
}

export function formatEntregaDeliveryWarningMessage(
  issues: EntregaDeliveryReadinessIssue[],
  opts?: { puntoNombre?: string },
): string {
  if (issues.length === 0) return '';
  const punto = opts?.puntoNombre?.trim();
  const head = punto ? `Advertencia en el punto «${punto}»:` : 'Advertencia:';
  return `${head} ${issues.map((issue) => issue.message).join(' ')}`;
}
